<template>
  <div
    class="truncate"
    :title="title"
  >
    <template
      v-if="!compactMode && hasStatusLabel"
    >
      <p
        class="text-fg-base-fg22"
        :class="{
          'text-body-r-14-20': forParticipantInChat,
          'text-label-r-13-18': !forParticipantInChat,
          'flex! items-center gap-x-1': showCircle,
          'mt-0!': !lastSeen || forParticipantInChat,
        }"
      >
        <template v-if="showOnlineStatus">
          <span
            v-if="showCircle"
            class="h-3 w-3 flex-none rounded-full bg-comp-avatar-status-online"
          />
          <span :class="forParticipantInChat ? 'text-body-r-14-20' : 'text-label-r-13-18'">
            {{ $t('common.online') }}
          </span>
        </template>

        <template v-else-if="!isGuestPServed && statuses?.status === OwnStatus.ABSENT">
          <span
            v-if="showCircle"
            class="h-3 w-3 flex-none rounded-full bg-comp-avatar-status-do-not-disturb text-fg-base-fg22"
          />
          {{ $t('common.absent') }}
        </template>

        <template v-else-if="!isGuestPServed && statuses?.status === OwnStatus.DO_NOT_DISTURB">
          <span
            v-if="showCircle"
            class="h-3 w-3 flex-none rounded-full bg-comp-avatar-status-in-the-call"
          />
          {{ $t('common.dontDisturb') }}
        </template>

        <template v-else-if="showOfflineStatus">
          <div class="grid grid-cols-[1fr_auto] gap-1 items-center">
            <span
              v-if="showCircle"
              class="h-3 w-3 rounded-full border-2 border-comp-avatar-status-do-not-disturb bg-bg-base-bg1"
            />
            <span
              v-if="lastSeen"
              class="truncate"
            >
              {{ $t('common.wasOnlineIn', { lastSeen }) }}
            </span>
          </div>
        </template>
        <template v-if="statuses?.statusMessage && !withoutStatusMessage">
          <div
            v-if="!withoutStatusMessage"
            class="flex items-center"
            :title="statuses.statusMessage"
          >
            <span class="shrink-0 mr-1"> — </span>
            <span class="truncate">
              {{ statuses.statusMessage }}
            </span>
          </div>
        </template>
      </p>
    </template>

    <template v-else-if="compactMode">
      <template v-if="showOnlineStatus">
        <span
          v-if="showCircle"
          data-test="contact-status-circle"
          class="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-comp-avatar-status-online ring-1 ring-bg-base-bg1"
        />
      </template>

      <template v-else-if="!isGuestPServed && statuses?.status === OwnStatus.ABSENT">
        <span
          v-if="showCircle"
          class="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-comp-avatar-status-do-not-disturb"
        />
      </template>

      <template v-else-if="!isGuestPServed && statuses?.status === OwnStatus.DO_NOT_DISTURB">
        <span
          v-if="showCircle"
          class="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-comp-avatar-status-in-the-call ring-1 ring-bg-base-bg1"
        />
      </template>

      <template v-else-if="showOfflineStatus">
        <span
          v-if="showCircle"
          class="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-comp-avatar-status-do-not-disturb bg-bg-base-bg1"
        />
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import 'dayjs/locale/en-gb'
import { PServed } from '@wui/im'
import { toRefs } from 'vue'

import { OwnStatus } from '@/shared/types/store'

import { useContactStatusPresence } from '../lib/use-contact-status-presence'

const props = withDefaults(defineProps<{
  pServed: PServed,
  showCircle?: boolean,
  compactMode?: boolean,
  forParticipantInChat?: boolean
  withoutStatusMessage?: boolean
}>(), {
  showCircle: true,
  compactMode: false,
  forParticipantInChat: false,
  withoutStatusMessage: false,
})

const { pServed } = toRefs(props)

const {
  hasStatusLabel,
  isGuestPServed,
  lastSeen,
  showOfflineStatus,
  showOnlineStatus,
  statuses,
  title,
} = useContactStatusPresence(pServed)
</script>
